import { LocationNode } from 'Interfaces/Admin';

/**
 *  /json/admin/loadLocations returns a flat list where each node names its
 *  parent; the tree has to be assembled client side. Ported from
 *  UserView.loadLocations.
 */
export const buildLocationTree = (nodes: LocationNode[]): LocationNode[] => {
  const childrenByParent = new Map<string, LocationNode[]>();
  const roots: LocationNode[] = [];

  nodes.forEach((node) => {
    if (node.parent === '0') {
      roots.push({ ...node });
      return;
    }
    const siblings = childrenByParent.get(node.parent);
    if (siblings) {
      siblings.push({ ...node });
    } else {
      childrenByParent.set(node.parent, [{ ...node }]);
    }
  });

  const seen = new Set<string>();
  const attach = (node: LocationNode) => {
    if (seen.has(node.key)) {
      return;
    }
    seen.add(node.key);
    const children = childrenByParent.get(node.key);
    if (children) {
      node.children = children;
      children.forEach(attach);
    }
  };
  roots.forEach(attach);

  return roots;
};

/** Depth of each node, keyed by node key, used to enforce the same-level rule. */
export const levelsByKey = (roots: LocationNode[]): Map<string, number> => {
  const levels = new Map<string, number>();
  const walk = (nodes: LocationNode[], depth: number) => {
    nodes.forEach((node) => {
      levels.set(node.key, depth);
      if (node.children) {
        walk(node.children, depth + 1);
      }
    });
  };
  walk(roots, 0);
  return levels;
};

/**
 *  A user's assigned locations must all sit at the same level of the hierarchy,
 *  so a newly checked node from a different level replaces the selection rather
 *  than being rejected outright.
 */
export const applySameLevelRule = (
  previous: string[], next: string[], levels: Map<string, number>,
): { keys: string[]; rejected: boolean } => {
  const added = next.filter((key) => !previous.includes(key));
  if (added.length === 0 || next.length <= 1) {
    return { keys: next, rejected: false };
  }
  const addedLevel = levels.get(added[0]);
  const conflicting = next.some(
    (key) => !added.includes(key) && levels.get(key) !== addedLevel,
  );
  return conflicting ? { keys: previous, rejected: true } : { keys: next, rejected: false };
};
